import { TextButton } from '@creditornot/cb-button';
import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import MappingConfigManagement from './MappingConfigManagement';
import SftpIntegrationIntroduction from './SftpIntegrationIntroduction';
import SftpUserKeysModal from './SftpUserKeysModal';
import SftpUserSshKeys from './SftpUserSshKeys';
import ShowSftpCredentialsModal from './ShowSftpCredentialsModal';
import VenueSftpConfigManagement from './VenueSftpConfigManagement';
import useSftpIntegrationUser from '../hooks/useSftpIntegrationUser';

type Props = {
  merchantId: string;
};

const Root = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 26px 24px;
  color: ${cssThemedValue('textPrimary')};
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const StyledTextButton = styled(TextButton)`
  margin: 0 8px;
`;

type SshKeyModalState = {
  createMode: boolean;
  isOpen: boolean;
};

const SftpIntegrationManagement = ({ merchantId }: Props) => {
  const [{ createMode, isOpen }, setSshKeyModalState] = useState<SshKeyModalState>({
    createMode: false,
    isOpen: false,
  });
  const [isShowCredentialsOpen, setIsShowCredentialsOpen] = useState(false);
  const { data: sftpIntegrationUser, isLoading, refresh } = useSftpIntegrationUser(merchantId);

  return (
    <Root>
      <Header>
        <T.Heading6>
          <FormattedMessage id="merchant-integrations.sftp.management.heading" />
        </T.Heading6>
        <T.Heading6>
          {sftpIntegrationUser && (
            <>
              <StyledTextButton onClick={() => setIsShowCredentialsOpen(true)}>
                <FormattedMessage id="merchant-integrations.show-credentials" />
              </StyledTextButton>
              <StyledTextButton onClick={() => setSshKeyModalState({ createMode: false, isOpen: true })}>
                <FormattedMessage id="merchant-integrations.sftp.management.add-ssh-key" />
              </StyledTextButton>
            </>
          )}
        </T.Heading6>
      </Header>
      {isLoading && (
        <LoadingSpinnerContainer>
          <Spinner size="large" />
        </LoadingSpinnerContainer>
      )}

      {!isLoading && (
        <>
          {sftpIntegrationUser != null ? (
            <>
              <SftpUserSshKeys
                sftpIntegrationUser={sftpIntegrationUser}
                merchantId={merchantId}
                onDelete={() => refresh()}
              />
              <MappingConfigManagement merchantId={merchantId} />
              <VenueSftpConfigManagement merchantId={merchantId} />
            </>
          ) : (
            <SftpIntegrationIntroduction
              onOpenSftpIntegrationModal={() => setSshKeyModalState({ createMode: true, isOpen: true })}
            />
          )}
        </>
      )}

      <SftpUserKeysModal
        isOpen={isOpen}
        createMode={createMode}
        onClose={() => {
          setSshKeyModalState({ createMode: false, isOpen: false });
          refresh();
        }}
        merchantId={merchantId}
      />
      {sftpIntegrationUser && (
        <ShowSftpCredentialsModal
          isOpen={isShowCredentialsOpen}
          onClose={() => {
            setIsShowCredentialsOpen(false);
          }}
          userName={sftpIntegrationUser?.username}
        />
      )}
    </Root>
  );
};

export default SftpIntegrationManagement;
